import { api } from '../../lib/api-client';

export interface UserRow {
  uid: string;
  username: string;
  name: string;
  email: string | null;
  employeeNo: string;
  isActive: boolean;
  /** Locked by failed sign-ins. Cleared by resetting the password. */
  isLocked: boolean;
  roles: { uid: string; code: string; name: string }[];
  branches: { uid: string; name: string }[];
  lastLoginAt: string | null;
}

export interface RoleRow {
  uid: string;
  code: string;
  name: string;
  /** Seeded roles from the blueprint. Editable, but not casually. */
  isSystem: boolean;
  userCount: number;
  permissions: string[];
}

export interface PermissionOption {
  code: string;
  /** The module it belongs to — `parts.issue` groups under `parts`. */
  group: string;
  action: string;
}

export interface BranchOption {
  uid: string;
  name: string;
  code: string;
}

export const fetchUsers = (): Promise<UserRow[]> => api<UserRow[]>('/users', { withoutBranch: true });

export const fetchRoles = (): Promise<RoleRow[]> => api<RoleRow[]>('/roles', { withoutBranch: true });

export const fetchPermissionCatalogue = (): Promise<PermissionOption[]> =>
  api<PermissionOption[]>('/roles/permissions', { withoutBranch: true });

export const fetchBranchOptions = (): Promise<BranchOption[]> =>
  api<BranchOption[]>('/branches', { withoutBranch: true });

export interface CreateUserInput {
  username: string;
  name: string;
  employeeNo: string;
  email?: string;
  password: string;
  roleUids: string[];
  branchUids: string[];
}

export const createUser = (input: CreateUserInput): Promise<UserRow> =>
  api<UserRow>('/users', { method: 'POST', body: input, withoutBranch: true });

export const updateUser = (
  uid: string,
  input: {
    name?: string;
    email?: string;
    isActive?: boolean;
    roleUids?: string[];
    branchUids?: string[];
  },
): Promise<UserRow> =>
  api<UserRow>(`/users/${uid}`, { method: 'PATCH', body: input, withoutBranch: true });

export const setUserPassword = (uid: string, password: string): Promise<void> =>
  api<undefined>(`/users/${uid}/password`, { method: 'POST', body: { password }, withoutBranch: true });

export const createRole = (input: {
  code: string;
  name: string;
  permissions: string[];
}): Promise<RoleRow> => api<RoleRow>('/roles', { method: 'POST', body: input, withoutBranch: true });

export const updateRole = (
  uid: string,
  input: { name?: string; permissions?: string[] },
): Promise<RoleRow> =>
  api<RoleRow>(`/roles/${uid}`, { method: 'PATCH', body: input, withoutBranch: true });

export const deleteRole = (uid: string): Promise<void> =>
  api<undefined>(`/roles/${uid}`, { method: 'DELETE', withoutBranch: true });

export interface CompanyProfile {
  legalName: string;
  registrationNo: string | null;
  tin: string | null;
  email: string | null;
  website: string | null;
  documentFooter: string | null;
}

/** Only the fields that changed. A blank string clears an optional field. */
export type UpdateCompanyInput = Partial<{ [K in keyof CompanyProfile]: string }>;

export const fetchCompany = (): Promise<CompanyProfile> =>
  api<CompanyProfile>('/iam/company', { withoutBranch: true });

export const saveCompany = (input: UpdateCompanyInput): Promise<CompanyProfile> =>
  api<CompanyProfile>('/iam/company', { method: 'PUT', body: input, withoutBranch: true });

export interface StaffRowCheck {
  line: number;
  employeeNo: string;
  name: string;
  username: string;
  roles: string[];
  branches: string[];
  status: 'CREATE' | 'EXISTS' | 'INVALID';
  problems: string[];
  /** Worth knowing but not stopping the import, such as a deactivated account. */
  notes: string[];
}

export interface StaffImportCheck {
  rows: StaffRowCheck[];
  toCreate: number;
  existing: number;
  invalid: number;
}

export interface CreatedStaff {
  uid: string;
  employeeNo: string;
  name: string;
  username: string;
  /** Shown once. The server never stores it; closing the drawer loses it. */
  temporaryPassword: string;
  roles: string[];
  branches: string[];
}

export const checkStaffImport = (csv: string): Promise<StaffImportCheck> =>
  api<StaffImportCheck>('/users/import/check', { method: 'POST', body: { csv }, withoutBranch: true });

export const importStaff = (csv: string): Promise<{ created: CreatedStaff[]; existing: number }> =>
  api<{ created: CreatedStaff[]; existing: number }>('/users/import', {
    method: 'POST',
    body: { csv },
    withoutBranch: true,
  });
